/**
 * Cliente de la validación documental SOBRE BLOQUES (V1).
 *
 * Hoy un validador comenta el documento entero. Esto le agrega lo único que le
 * faltaba: poder tocar un PÁRRAFO y que el comentario quede colgado de ese
 * párrafo. El ancla es un acto aparte, POSTERIOR al aporte: el comentario se
 * crea por los endpoints de siempre (`/actividades/{id}/aportes`, la cola de
 * media) y recién después se llama a `anclar`. Si el ancla falla, el comentario
 * ya está a salvo y solo queda sin dirección — «ancla huérfana», que es un
 * estado válido y previsto (SSOT §5.2). Nunca se sacrifica el comentario por
 * el ancla.
 *
 * REGLA DE ORO — el modo con bloques es OPCIONAL y se apaga solo. Las tres
 * validaciones vivas del Consejo de la Carrera Judicial (25 comentarios reales)
 * no tienen documento cargado en ÁGORA: para ellas el backend responde 404 y
 * {@link documentoDeValidacion} devuelve `null`, que es la señal para que la
 * página se comporte exactamente como hoy. Por eso el 404 acá NO es un error:
 * es el camino normal de la mayoría de las validaciones.
 *
 * El ancla apunta a `id_estable`, nunca al `id` de la fila: el id de la fila
 * muere con la versión del documento, el estable la atraviesa.
 */

import { ApiError, apiFetch } from './api';

// ─── Lectura: el documento y sus bloques ────────────────────────────────────

/** Qué es este bloque dentro del documento. Decide cómo se ve, no qué hace. */
export type BloqueTipo = 'parrafo' | 'titulo' | 'fila' | 'lista';

/** Un párrafo del documento: la unidad que se toca y de la que se cuelga todo. */
export interface Bloque {
  /** Identidad ESTABLE entre versiones. Es lo que apunta el ancla. */
  id_estable: string;
  orden: number;
  tipo: BloqueTipo | string;
  texto: string;
  /** Jerarquía del título (1 = el más alto). Solo en `tipo: 'titulo'`. */
  nivel: number | null;
  /** Numeración detectada: 'RF-041', '4.1', 'Artículo 12'. Como lo nombra la gente. */
  ref: string | null;
  /** Se llena en V2 (resumen de una línea del bloque). Hoy siempre null. */
  resumen_linea: string | null;
}

export interface VersionDocumento {
  version_id: string;
  numero: number;
  /** sha256 del texto normalizado: dos versiones iguales tienen el mismo hash. */
  hash: string;
  estado: 'publicada' | 'reemplazada' | string;
  publicada_at: string;
}

export interface DocumentoResumen {
  id: string;
  titulo: string;
  mime: string | null;
  created_at: string;
  versiones: number;
  bloques: number;
  version_actual: VersionDocumento | null;
}

export interface DocumentoConBloques {
  documento: DocumentoResumen;
  version: VersionDocumento;
  bloques: Bloque[];
}

// ─── Lectura: el pulso por párrafo (SQL determinista, jamás IA) ──────────────

export interface BloqueResumen {
  bloque_id_estable: string;
  comentarios: number;
  personas: number;
}

export interface ResumenBloques {
  version_id: string;
  bloques_total: number;
  bloques_con_comentario: number;
  comentarios_anclados: number;
  /** Comentarios de la validación que no quedaron colgados de ningún párrafo. */
  comentarios_sin_anclar: number;
  /** Fracción de párrafos con al menos un comentario (0.0–1.0). */
  cobertura: number;
  por_bloque: BloqueResumen[];
}

/** Una intervención de la validación, con su dirección si la tiene. */
export interface ComentarioValidacion {
  aporte_id: string;
  autor: string | null;
  tipo: string;
  /** Lo escrito, o lo dicho ya transcrito. Null mientras se transcribe. */
  texto: string | null;
  tiene_audio: boolean;
  created_at: string;
  /** Null = comentario sin ubicar: habla del documento, no de un párrafo. */
  bloque_id_estable: string | null;
  origen_ancla: string | null;
  huella: string | null;
}

// ─── Escritura: el ancla ────────────────────────────────────────────────────

export interface NuevaAncla {
  aporte_id: string;
  bloque_id_estable: string;
  /** Sin offsets el ancla cubre el párrafo entero, que es el gesto de V1. */
  offset_ini?: number;
  offset_fin?: number;
  /** Texto citado. Si no va, el servidor lo deriva del bloque. */
  huella?: string;
  prefijo?: string;
  sufijo?: string;
}

/**
 * Un párrafo que un comentario podría estar nombrando, resuelto por el léxico
 * del documento —sin IA, en milisegundos—.
 *
 * La confianza decide qué hace la interfaz, y la escala importa: por encima de
 * 0.9 la referencia es inequívoca y se ancla sola; entre 0.5 y 0.9 hay una
 * corrección de por medio (típicamente una sigla que el reconocedor de voz oyó
 * mal) y hay que PREGUNTAR; por debajo no se muestra nada. Un chip que propone
 * cualquier cosa con tal de proponer algo enseña a ignorarlo.
 */
export interface SugerenciaBloque {
  bloque_id_estable: string;
  ref: string;
  confianza: number;
  /** Qué se reconoció, para poder decírselo a la persona. */
  porque: string;
  orden: number;
  texto: string;
}

export interface Ancla {
  id: string;
  aporte_id: string;
  version_id: string;
  bloque_id_estable: string;
  offset_ini: number | null;
  offset_fin: number | null;
  huella: string | null;
  /** puntero (lo mandó el autor) | manual (lo movió un facilitador) | interpretacion | huerfana */
  origen: string;
  confirmada_por_usuario: boolean;
  created_at: string;
  actualizada_at: string | null;
  /** True si el aporte ya tenía ancla y esta llamada la cambió de sitio. */
  movida: boolean;
}

/** Datos de la subida de un documento a un proceso. */
export interface SubidaDocumento {
  file: File;
  titulo: string;
  /**
   * Validación a la que se engancha el documento. Si va, la actividad queda
   * apuntando a la versión recién publicada y su visor pasa a modo bloques.
   * Sin esto el documento queda cargado pero suelto.
   */
  actividadId?: string;
}

// ─── Llaves de caché ────────────────────────────────────────────────────────

export const documentosKeys = {
  delProceso: (procesoId: string) => ['documentos', procesoId] as const,
  bloques: (documentoId: string) => ['documento-bloques', documentoId] as const,
  deValidacion: (actividadId: string) => ['validacion-documento', actividadId] as const,
  resumen: (actividadId: string) => ['validacion-resumen', actividadId] as const,
  comentarios: (actividadId: string) => ['validacion-comentarios', actividadId] as const,
};

// ─── API ────────────────────────────────────────────────────────────────────

/**
 * Un 404 acá significa «esta validación no tiene documento», no un fallo. Se
 * traduce a `null` para que quien consuma decida el modo con un `if` y no con
 * un try/catch. Cualquier otro error (403, 500, red) sigue subiendo.
 */
async function nullEn404<T>(promesa: Promise<T>): Promise<T | null> {
  try {
    return await promesa;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

export const documentosApi = {
  /**
   * El documento y los bloques de ESTA validación, o `null` si no tiene uno
   * cargado en ÁGORA (el caso de las tres validaciones vivas del CCJ).
   *
   * También devuelve `null` cuando el documento existe pero quedó sin bloques
   * utilizables —versión borrada, ingesta a medias—: un visor vacío no le
   * sirve a nadie y la decisión de la página es binaria (hay documento que
   * tocar, o se comenta el documento entero como siempre).
   */
  deValidacion: async (actividadId: string): Promise<DocumentoConBloques | null> => {
    const d = await nullEn404(
      apiFetch<DocumentoConBloques>(`/validaciones/${actividadId}/documento`),
    );
    if (!d || d.bloques.length === 0) return null;
    return d;
  },

  /** Cuántos comentarios cayó en cada párrafo. `null` si no hay documento. */
  resumen: (actividadId: string): Promise<ResumenBloques | null> =>
    nullEn404(apiFetch<ResumenBloques>(`/validaciones/${actividadId}/bloques/resumen`)),

  /**
   * QUÉ dijo cada quien, no solo cuántos. Incluye los comentarios sin ancla,
   * con `bloque_id_estable` en null: son datos que entraron y que alguien tiene
   * que ubicar, así que no se esconden. Solo para quien facilita.
   */
  comentarios: (actividadId: string): Promise<ComentarioValidacion[]> =>
    apiFetch<ComentarioValidacion[]>(`/validaciones/${actividadId}/comentarios`),

  /**
   * Qué párrafo nombró este comentario. Determinista: no gasta cuota de IA y
   * responde en milisegundos, que es lo que permite preguntar mientras la
   * persona sigue mirando la pantalla. Lista vacía es una respuesta normal.
   */
  sugerirBloque: (actividadId: string, texto: string): Promise<SugerenciaBloque[]> =>
    apiFetch<SugerenciaBloque[]>(`/validaciones/${actividadId}/sugerir-bloque`, {
      method: 'POST',
      json: { texto },
    }),

  /**
   * Cuelga un comentario YA CREADO de un párrafo. Idempotente por aporte: si
   * ese comentario ya tenía ancla, la mueve (`movida: true`) en vez de duplicar.
   * Se llama después de crear el aporte, nunca en su lugar.
   */
  anclar: (actividadId: string, cuerpo: NuevaAncla): Promise<Ancla> =>
    apiFetch<Ancla>(`/validaciones/${actividadId}/anclar`, { method: 'POST', json: cuerpo }),

  /** Documentos cargados en un proceso (Cabina). */
  delProceso: (procesoId: string): Promise<DocumentoResumen[]> =>
    apiFetch<DocumentoResumen[]>(`/procesos/${procesoId}/documentos`),

  /** Bloques de la última versión publicada de un documento suelto. */
  bloques: (documentoId: string): Promise<DocumentoConBloques> =>
    apiFetch<DocumentoConBloques>(`/documentos/${documentoId}/bloques`),

  /**
   * Sube el documento, lo parte en párrafos y publica su versión 1. Multipart:
   * el navegador pone el boundary, nunca fijamos Content-Type a mano.
   */
  subir: (procesoId: string, datos: SubidaDocumento): Promise<DocumentoResumen> => {
    const form = new FormData();
    form.append('file', datos.file, datos.file.name);
    form.append('titulo', datos.titulo);
    if (datos.actividadId) form.append('actividad_id', datos.actividadId);
    return apiFetch<DocumentoResumen>(`/procesos/${procesoId}/documentos`, {
      method: 'POST',
      form,
    });
  },

  /** Apunta una validación existente a la última versión de un documento ya cargado. */
  vincular: (actividadId: string, documentoId: string): Promise<DocumentoConBloques> =>
    apiFetch<DocumentoConBloques>(`/validaciones/${actividadId}/documento`, {
      method: 'POST',
      json: { documento_id: documentoId },
    }),
};

// ─── Auxiliares de presentación ─────────────────────────────────────────────

/** Índice `id_estable -> conteos` para pintar el contador de cada párrafo sin O(n²). */
export function indicePorBloque(
  resumen: ResumenBloques | null | undefined,
): Map<string, BloqueResumen> {
  const m = new Map<string, BloqueResumen>();
  for (const b of resumen?.por_bloque ?? []) m.set(b.bloque_id_estable, b);
  return m;
}

/**
 * Contexto de 32 caracteres a cada lado del fragmento, tal como lo guarda el
 * backend cuando el cliente no lo manda. Lo calculamos igual del lado del
 * cliente para que el ancla viaje completa desde el primer intento: es lo que
 * permitirá re-anclar por coincidencia difusa cuando el documento cambie.
 */
export function contextoDelBloque(
  texto: string,
  ini = 0,
  fin = texto.length,
): { huella: string; prefijo: string; sufijo: string } {
  const a = Math.max(0, Math.min(ini, texto.length));
  const b = Math.max(a, Math.min(fin, texto.length));
  return {
    huella: texto.slice(a, b),
    prefijo: texto.slice(Math.max(0, a - 32), a),
    sufijo: texto.slice(b, b + 32),
  };
}
